<template>
	<VerticalLayout>
		<Select
			class="width-100"
			v-model:value="selectedAddress"
			:options="addresses"
			mode="multiple"
			:allowClear="true"
			:placeholder="$t('userAddrSessFilter.addressFilter')"
			@change="onChange"
			:getPopupContainer="getPopupContainer"
			show-search
		/>
		<Select
			v-if="showUsernameFilter"
			class="width-100"
			v-model:value="selectedUsername"
			:options="usernames"
			mode="multiple"
			:allowClear="true"
			:placeholder="$t('userAddrSessFilter.usernameFilter')"
			@change="onChange"
			:getPopupContainer="getPopupContainer"
			show-search
		/>
		<Select
			class="width-100"
			v-model:value="selectedSessionId"
			:options="sessionIds"
			mode="multiple"
			:allowClear="true"
			:placeholder="$t('userAddrSessFilter.sessionIdFilter')"
			@change="onChange"
			:getPopupContainer="getPopupContainer"
			show-search
		/>
	</VerticalLayout>
</template>

<script>
import { ref, toRef, computed, watch } from 'vue';
import { Select } from 'ant-design-vue';

import Utils from '@/api/Utils';
import { useUserStore } from '@/stores/user';

import { usePopupContainer } from '@/utils/usePopupContainer';

import VerticalLayout from '@/components/common/VerticalLayout';

export default {
	components: { Select, VerticalLayout },
	props: {
		items: {
			type: Array,
			default: () => [],
		},
		addressField: {
			type: String,
			required: true,
		},
		usernameField: {
			type: String,
			required: true,
		},
		sessionIdField: {
			type: String,
			required: true,
		},
		onChange: {
			type: Function,
			required: false,
			default: () => {},
		},
	},
	setup(props, { expose }) {
		const { getPopupContainer } = usePopupContainer();
		const userStore = useUserStore();
		const showUsernameFilter = computed(() => userStore.role != 'rover');

		const addresses = ref([]);
		const selectedAddress = ref([]);
		const usernames = ref([]);
		const selectedUsername = ref([]);
		const sessionIds = ref([]);
		const selectedSessionId = ref([]);
		const itemsRef = toRef(props, 'items');

		watch(itemsRef, () => {
			const addressSet = new Set();
			const usernameSet = new Set();
			const sessionIdSet = new Set();
			for (const item of itemsRef.value) {
				const sessionId = item[props.sessionIdField];
				if (sessionId != null && sessionId !== '') {
					sessionIdSet.add(sessionId);
				}
				const username = item[props.usernameField];
				if (username != null && username !== '') {
					usernameSet.add(username);
				}
				const address = item[props.addressField];
				if (address != null && address !== '') {
					addressSet.add(address);
				}
			}
			addresses.value = Utils.sortArray(Array.from(addressSet).map(item => ({
				value: item,
				label: item,
			})), Utils.objectFieldMultiSorter('value'));
			selectedAddress.value = selectedAddress.value.filter(v => addresses.value.some(opt => opt.value === v));
			sessionIds.value = Utils.sortArray(Array.from(sessionIdSet).map(item => ({
				value: item,
				label: item,
			})), Utils.objectFieldMultiSorter('value'));
			selectedSessionId.value = selectedSessionId.value.filter(v => sessionIds.value.some(opt => opt.value === v));
			usernames.value = Utils.sortArray(Array.from(usernameSet).map(item => ({
				value: item,
				label: item,
			})), Utils.objectFieldMultiSorter('value'));
			selectedUsername.value = selectedUsername.value.filter(v => usernames.value.some(opt => opt.value === v));
		}, { immediate: true });

		const filteredItems = computed(() => {
			let result = itemsRef.value;
			if (selectedAddress.value.length > 0) {
				const set = new Set(selectedAddress.value);
				result = result.filter(item => {
					const address = item[props.addressField];
					return address != null && set.has(address);
				});
			}
			if (showUsernameFilter.value && selectedUsername.value.length > 0) {
				const set = new Set(selectedUsername.value);
				result = result.filter(item => {
					const username = item[props.usernameField];
					return username != null && set.has(username);
				});
			}
			if (selectedSessionId.value.length > 0) {
				const set = new Set(selectedSessionId.value);
				result = result.filter(item => {
					const sessionId = item[props.sessionIdField];
					return sessionId != null && set.has(sessionId);
				});
			}
			return result;
		});

		const setSessionIdOnly = sessionId => {
			selectedAddress.value = [];
			selectedUsername.value = [];
			selectedSessionId.value = sessionId != null ? [sessionId] : [];
			props.onChange();
		};

		expose({
			filteredItems,
			setSessionIdOnly,
		});

		return {
			addresses,
			selectedAddress,
			usernames,
			selectedUsername,
			sessionIds,
			selectedSessionId,
			getPopupContainer,
			showUsernameFilter,
		};
	},
};
</script>
